import { create } from "zustand";
import { extensionFor, RenderEmpty, RenderUnsupported, renderFilm } from "./renderFilm.ts";
import { DEFAULT_SETTINGS, frameRate, type Settings } from "./settings.ts";

/**
 * The film being made, kept outside the screen that asks for it.
 *
 * Every tab in this app is its own route, and each of them renders its own
 * `ShellLayout` — `Feed` and `Share` are different component types in the
 * same position, so a tab change is not a re-render but a remount of
 * everything, all four screens included. Held in `useState`, a film did not
 * survive the swipe that took you away from it: a render in flight was
 * aborted by the unmount, and a finished one had its object URL revoked
 * before Save could be pressed. Thirty seconds of recording, thrown away by
 * the gesture this app is navigated with.
 *
 * Two things follow from the film outliving its screen, and both are handled
 * rather than hoped about: it belongs to ONE album, so it carries the id it
 * was made from, and it has to be ended by hand at the door — nothing
 * unmounts to end it any more. See `discardFilm`.
 */

/** Where the film is in its making. */
export type Stage = "choosing" | "rendering" | "done" | "failed";

/** Why it could not be made. */
export type Failure = "unsupported" | "empty" | "other";

export type Film = {
  /** An object URL: a lease on a file held in this document's memory until
   *  it is revoked or the tab is closed. */
  url: string;
  /** The same bytes, for the share sheet — which takes a file, not an
   *  address. One copy: the URL is a lease on this blob, not a second one. */
  blob: Blob;
  extension: string;
  /** Frames that would not load. A film missing one day out of four hundred
   *  is still the film somebody asked for, but they are told. */
  skipped: number;
  /** The album it was made from. A film of one album must never be offered
   *  under another's name, and the screen it was made on can be left. */
  albumId: string;
  /** And the settings it was made with, for the same reason one step in:
   *  the range and the length can be changed after a film is finished, and
   *  Save would go on handing over the one made before the change — a film
   *  of a different length than the line above the button says. */
  settings: Settings;
};

type FilmState = {
  settings: Settings;
  stage: Stage;
  progress: number;
  /**
   * Whose film is being recorded while `stage` is `rendering`. The album
   * can be changed under a render — from the diary's chips, from an
   * album's own Film door — and the film tab then shows the other album's
   * frames over the first one's progress and Cancel: "Recording… 27%"
   * over twelve days when sixty-four were being recorded, and a Cancel
   * that would end the other album's film.
   */
  renderingAlbumId: string | null;
  film: Film | null;
  failure: Failure;
  choose: (change: Partial<Settings>) => void;
  /** Records the run of frames given, oldest first. */
  render: (request: { albumId: string; urls: readonly string[] }) => Promise<void>;
  cancel: () => void;
};

/**
 * Outside the state because nothing renders it, and one render at a time
 * means one controller: `render` refuses to start a second while the first
 * is running.
 */
let controller: AbortController | null = null;

export const useFilm = create<FilmState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  stage: "choosing",
  progress: 0,
  renderingAlbumId: null,
  film: null,
  failure: "other",

  choose: (change) => set((state) => ({ settings: { ...state.settings, ...change } })),

  render: async ({ albumId, urls }) => {
    if (urls.length === 0 || get().stage === "rendering") return;
    const { settings } = get();
    discardFilm();
    set({ progress: 0, stage: "rendering", renderingAlbumId: albumId });
    controller = new AbortController();
    try {
      const result = await renderFilm({
        urls,
        frame: settings.frame,
        fps: frameRate(urls.length, settings.length),
        watermarked: settings.mark,
        onProgress: (progress) => set({ progress }),
        signal: controller.signal,
      });
      set({
        film: {
          url: URL.createObjectURL(result.blob),
          blob: result.blob,
          extension: extensionFor(result.mimeType),
          skipped: result.skipped,
          albumId,
          settings,
        },
        stage: "done",
        renderingAlbumId: null,
      });
    } catch (error) {
      if ((error as Error)?.name === "AbortError") {
        set({ stage: "choosing", renderingAlbumId: null });
        return;
      }
      set({
        failure:
          error instanceof RenderUnsupported
            ? "unsupported"
            : error instanceof RenderEmpty
              ? "empty"
              : "other",
        stage: "failed",
        renderingAlbumId: null,
      });
    } finally {
      controller = null;
    }
  },

  cancel: () => controller?.abort(),
}));

/**
 * Ends the film: the memory back, the screen at its settings again.
 *
 * Called at the two doors out of the session — signing out and deleting the
 * account — beside `queryClient.clear()`, for the same reason. A film is a
 * photograph of somebody's face for every day of a year, and it used to be
 * ended by the screen unmounting; now that it survives that, the only thing
 * that can end it is being told to.
 *
 * A render still running is ended too. It went on past the door, and set
 * a finished film of the account just left into the store a few seconds
 * later — for whoever signed in next on this tab to hold in memory, under
 * an album id that never matched theirs, until their own first render
 * took the memory back.
 */
export function discardFilm(): void {
  controller?.abort();
  const { film } = useFilm.getState();
  if (film) URL.revokeObjectURL(film.url);
  useFilm.setState({ film: null, stage: "choosing", progress: 0, renderingAlbumId: null });
}
